import { z } from 'zod';

/**
 * Robot passport (#163) — the client mirror of `GET /api/licenses/serial/:serial/passport`.
 *
 * Kept field-for-field in sync with the server handler
 * (`rust/server/src/routes/licenses/read.rs::passport`); a drift here is the
 * `ls!355` class of bug, so any server change must land on both sides at once.
 */
export const passportKindSchema = z.enum(['robot', 'software']);

/** The licence lifecycle the passport reports (plus the pre-licence birth state). */
export const passportStateSchema = z.enum([
  'awaiting_activation',
  'pending',
  'permitted',
  'active',
  'expired',
  'revoked',
]);

export const passportBirthSchema = z.object({
  model: z.string().nullable(),
  source: z.string().nullable(),
  registeredAt: z.string(),
  fingerprintHash: z.record(z.unknown()),
});

export const passportLicenseSchema = z.object({
  id: z.string(),
  status: z.string(),
  type: z.string(),
  createdAt: z.string(),
  expiresAt: z.string().nullable(),
});

/** One append-only history entry: who, when, from where. */
export const passportHistoryEntrySchema = z.object({
  id: z.string(),
  timestamp: z.string(),
  event: z.string(),
  operation: z.string(),
  /** `birth` (public report) · `crm` (sale intake) · `admin` (operator action). */
  channel: z.enum(['birth', 'crm', 'admin']),
  performedBy: z.string(),
  ipAddress: z.string().nullable(),
  metadata: z.record(z.unknown()),
});

export const robotPassportSchema = z.object({
  serial: z.string(),
  kind: passportKindSchema,
  state: passportStateSchema,
  birth: passportBirthSchema.nullable(),
  license: passportLicenseSchema.nullable(),
  /** One page of the append-only history, newest first. */
  history: z.array(passportHistoryEntrySchema),
  /** Total history rows on the server (all pages). */
  historyTotal: z.number(),
  /** The 1-based page `history` holds. */
  historyPage: z.number(),
  /** The page size the server applied. */
  historyLimit: z.number(),
  /** Number of pages of history. */
  historyPages: z.number(),
});

export type PassportKind = z.infer<typeof passportKindSchema>;
export type PassportState = z.infer<typeof passportStateSchema>;
export type PassportBirth = z.infer<typeof passportBirthSchema>;
export type PassportHistoryEntry = z.infer<typeof passportHistoryEntrySchema>;
export type RobotPassport = z.infer<typeof robotPassportSchema>;
